// JsonDisplay.jsx
import React, { useState, useEffect } from 'react';
import { ReactComponent as EditIcon } from 'pages/marketing/Pages/Form/edit.svg';
import './style.scss';

const sortedDataKeys = [
  "title",
  "description",
  "backgroundColor",
  "backgroundGradientColorA",
  "backgroundGradientColorB",
  "backgroundImage",
  "illustrationImage",
  "voucherWalletButtonText",
  "voucherWalletButtonIcon",
  "ctaButtonText",
  "ctaButtonTextColor",
  "ctaButtonColor",
  "ctaButtonLink",
]

function useImageDimensions(url) {
  const [dimensions, setDimensions] = useState({ width: null, height: null });

  useEffect(() => {
    if (url) {
			const img = new window.Image();
			img.src = url;
			img.onload = () => {
				setDimensions({ width: img.width, height: img.height });
			};
			img.onerror = () => {
				setDimensions({ width: null, height: null });
			};
		}
  }, [url]);

  return dimensions;
}

const rearrange = (array) => {
  const map = new Map(array);

  const ordered = sortedDataKeys
    .map(key => (map.has(key) ? [key, map.get(key)] : null))
    .filter(Boolean);

  // Add any remaining keys not in sortedDataKeys
  const remaining = array.filter(([key]) => !sortedDataKeys.includes(key));

  return [...ordered, ...remaining];
};

const formatCamelCase = (key) => {
	if (!key) {
		return '';
	}
	
	const specialCases = {
		'id': 'ID',
		'url': 'URL',
		'cta': 'CTA',
	};
	
	// Add space before capital letters and trim
	const withSpaces = key.replace(/([A-Z])/g, ' $1').trim();
	
	// Capitalize first letter of each word and handle special cases
	const displayText = withSpaces
		.split(' ')
		.map(word => {
			const lowerWord = word.toLowerCase();
			return specialCases[lowerWord] || 
				(word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
		})
		.join(' ');
  
  if (key === 'backgroundImage') {
    return `${displayText} (Old version of voucher center)`
  } else if (key === 'illustrationImage') {
    return `${displayText} (New version of voucher center)`
  }
  return displayText;
  
};

function ImageDimensions({ url }) {
  const { width, height } = useImageDimensions(url);

  return (
    <span className='no-style-text'>
      ({width && height
        ? `Width: ${width}px, Height: ${height}px`
        : "Loading image dimensions..."})
    </span>
  );
}

function ConfigDisplay({ data, title, toggleConfigEditMode }) {
  
	const renderValue = (value) => {
		if (typeof value === 'string' && value.startsWith('http')) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="config-link">
          {value} <ImageDimensions url={value} />
        </a>
      );
		}
    
		return value;
	};

	const dataArr = Object.entries(data).filter(([_, value]) => !Array.isArray(value))
	const rearrangedData = rearrange(dataArr);

	return (
		<div className="config-display">
			<div className="config-title">
				<h2>{title}</h2>
				<button
					data-testid="edit-config-btn"
					onClick={(e) => {
						e.preventDefault()
						toggleConfigEditMode()
					}}>
					<EditIcon height={14} width={14} />
				</button>
			</div>
			<table className="config-table">
				<tbody>
					{rearrangedData.map(([key, value]) => (
							<tr key={key} className="config-row">
								<td className="config-key">
									{formatCamelCase(key)}
								</td>
								<td className="config-value">
									<div className='config-value-wrapper'>
										{renderValue(value)}
										{
											value?.startsWith('#') && value.length < 8 &&
												<div className='color-box' style={{background: value}}></div>
										}
									</div>
								</td>
							</tr>
						))}
				</tbody>
			</table>
		</div>
	);
}

export default ConfigDisplay;
